// src/documents/xlsx/parseXlsxDrawings.js
//
// Excel'in "Ekle > Şekiller" menüsünden eklenmiş line/connector shape'lerini
// xlsx'in xl/drawings/drawing*.xml dosyalarından çıkarır. Her shape iki cell
// anchor noktası (from/to) ile tanımlı; renderer bunları cell pozisyonlarına
// göre canvas pixel koordinatlarına dönüştürür.
//
// Sınırlama: sadece prst="line" tipi (düz bağlayıcı/connector) destekleniyor.
// Bezier eğrileri, şekiller (rect/oval) henüz desteklenmiyor — gerekirse
// genişletilebilir.

import JSZip from 'jszip';

// EMU = English Metric Units. 9525 EMU = 1 pixel @ 96 DPI
const EMU_PER_PX = 9525;

function parseAnchorPoint(xml, tagName) {
  const re = new RegExp(`<xdr:${tagName}>([\\s\\S]*?)</xdr:${tagName}>`);
  const m = xml.match(re);
  if (!m) return null;
  const inner = m[1];
  const colM = inner.match(/<xdr:col>(\d+)<\/xdr:col>/);
  const colOffM = inner.match(/<xdr:colOff>(-?\d+)<\/xdr:colOff>/);
  const rowM = inner.match(/<xdr:row>(\d+)<\/xdr:row>/);
  const rowOffM = inner.match(/<xdr:rowOff>(-?\d+)<\/xdr:rowOff>/);
  if (!colM || !rowM) return null;
  return {
    col:    parseInt(colM[1], 10),
    colOff: parseInt(colOffM?.[1] || '0', 10),
    row:    parseInt(rowM[1], 10),
    rowOff: parseInt(rowOffM?.[1] || '0', 10),
  };
}

function parseLineShape(anchorXml) {
  const from = parseAnchorPoint(anchorXml, 'from');
  const to   = parseAnchorPoint(anchorXml, 'to');
  if (!from || !to) return null;

  // prstGeom prst="line" mı kontrol et
  const isLine = /prstGeom\s+prst="(line|straightConnector1)"/.test(anchorXml);
  if (!isLine) return null;

  // xfrm — shape'in kendi frame'inde flipH/flipV uygulandıktan sonra rot
  // ile bbox merkezi etrafında döner, sonra <a:off>'a translate edilir.
  // Asıl line endpoints'i hesaplamak için bu sırayı doğru uygulamalıyız.
  const xfrmM = anchorXml.match(/<a:xfrm([^>]*)>([\s\S]*?)<\/a:xfrm>/);
  let flipH = false, flipV = false, rotDeg = 0;
  let off = null, ext = null;
  if (xfrmM) {
    const attrs = xfrmM[1];
    flipH = /flipH="1"/.test(attrs);
    flipV = /flipV="1"/.test(attrs);
    const rotM = attrs.match(/rot="(-?\d+)"/);
    if (rotM) rotDeg = parseInt(rotM[1], 10) / 60000;
    const offM = xfrmM[2].match(/<a:off\s+x="(-?\d+)"\s+y="(-?\d+)"/);
    const extM = xfrmM[2].match(/<a:ext\s+cx="(-?\d+)"\s+cy="(-?\d+)"/);
    if (offM) off = { x: parseInt(offM[1], 10), y: parseInt(offM[2], 10) };
    if (extM) ext = { cx: parseInt(extM[1], 10), cy: parseInt(extM[2], 10) };
  }

  // Style: line width (lnRef idx) — default thin = idx 1
  const lnW = anchorXml.match(/<a:ln\s+w="(\d+)"/);
  const widthEmu = lnW ? parseInt(lnW[1], 10) : 0; // 0 = use default

  // Color: a:srgbClr val="000000" varsa kullan; yoksa default siyah
  const srgb = anchorXml.match(/<a:srgbClr\s+val="([0-9A-Fa-f]{6})"/);
  const colorRgb = srgb ? srgb[1] : null;

  return {
    from, to,
    flipH, flipV, rotDeg,
    off, ext,
    widthEmu,
    colorRgb,
  };
}

/**
 * @param {ArrayBuffer|Uint8Array} xlsxBytes
 * @returns {Promise<Array<{from, to, widthEmu, colorRgb}>>}
 */
export async function parseXlsxDrawings(xlsxBytes) {
  try {
    const zip = await JSZip.loadAsync(xlsxBytes);
    const drawingFiles = zip.file(/xl\/drawings\/drawing\d+\.xml/);
    if (!drawingFiles || drawingFiles.length === 0) return [];

    const lines = [];
    for (const file of drawingFiles) {
      const xml = await file.async('string');
      // twoCellAnchor / oneCellAnchor / absoluteAnchor — şimdilik twoCellAnchor
      const anchorRe = /<xdr:twoCellAnchor[\s\S]*?<\/xdr:twoCellAnchor>/g;
      let m;
      while ((m = anchorRe.exec(xml)) !== null) {
        const shape = parseLineShape(m[0]);
        if (shape) lines.push(shape);
      }
    }
    return lines;
  } catch (err) {
    console.warn('[parseXlsxDrawings] hata:', err);
    return [];
  }
}

export const XLSX_DRAWINGS_EMU_PER_PX = EMU_PER_PX;
